import { getWarehouseStock } from './inventory';

export type MaterialType = 'RM' | 'PM' | 'STAGE';
export type MaterialFacilityType = 'WAREHOUSE' | 'VENDOR';

export type MaterialStockSnapshotRow = {
    materialId: string;
    materialName: string;
    sku: string | null;
    materialType: MaterialType;
    unit: string;
    category: string | null;
    facilityId: string;
    facilityType: MaterialFacilityType;
    facilityName: string | null;
    vendorRole: string | null;
    quantity: number;
    onHoldQuantity: number | null;
    updatedAt: string | null;
};

export type MaterialStockSnapshotResponse = {
    rows: MaterialStockSnapshotRow[];
};

export type MaterialStockSnapshotFilters = {
    materialType?: MaterialType;
    facilityIds?: string[];
    materialIds?: string[];
    search?: string;
};

/**
 * Fetches material (RM/PM) stock from POST /warehouse-stock with itemType='material'.
 * Groupings (per warehouse_id) are exploded into one MaterialStockSnapshotRow per facility,
 * which the production-planning pivot table consumes to build its column layout.
 */
export const getMaterialStockSnapshot = async (
    filters: MaterialStockSnapshotFilters = {},
): Promise<MaterialStockSnapshotResponse> => {
    const res = await getWarehouseStock({
        itemType: 'material',
        type: 'INTERNAL',
        materialType: filters.materialType,
        productIds: filters.materialIds,
        search: filters.search,
    });

    const rows: MaterialStockSnapshotRow[] = [];

    for (const item of res.data) {
        const groupings = item.groupings ?? {};
        const warehouseIds = Object.keys(groupings);

        if (warehouseIds.length === 0) {
            // Material exists in WSI but has no groupings — emit a single zero-stock row
            rows.push({
                materialId: item.product_id,
                materialName: item.product_name ?? item.product_id,
                sku: item.sku ?? null,
                materialType: (item.material_type as MaterialType) ?? 'RM',
                unit: item.unit ?? 'pcs',
                category: item.category ?? null,
                facilityId: '',
                facilityType: 'WAREHOUSE',
                facilityName: null,
                vendorRole: null,
                quantity: 0,
                onHoldQuantity: null,
                updatedAt: null,
            });
        } else {
            for (const facilityId of warehouseIds) {
                const g = groupings[facilityId];
                // Apply facilityIds filter client-side if provided (server doesn't filter by it yet)
                if (filters.facilityIds?.length && !filters.facilityIds.includes(facilityId)) {
                    continue;
                }
                rows.push({
                    materialId: item.product_id,
                    materialName: item.product_name ?? item.product_id,
                    sku: item.sku ?? null,
                    materialType: (item.material_type as MaterialType) ?? 'RM',
                    unit: item.unit ?? 'pcs',
                    category: item.category ?? null,
                    facilityId,
                    facilityType: (g.facility_type as MaterialFacilityType) ?? 'WAREHOUSE',
                    facilityName: g.warehouse_name ?? facilityId,
                    vendorRole: g.vendor_role ?? null,
                    quantity: g.quantity ?? 0,
                    onHoldQuantity: g.on_hold_stock ?? null,
                    updatedAt: null,
                });
            }
        }
    }

    return { rows };
};
